import moment from "moment-timezone";

type TimeUnit = {
  singular: string;
  plural: string;
  feminine: boolean;
};

const TIME_UNITS: Record<string, TimeUnit> = {
  minute: { singular: "minuto", plural: "minutos", feminine: false },
  hour: { singular: "hora", plural: "horas", feminine: true },
  day: { singular: "día", plural: "días", feminine: false },
  week: { singular: "semana", plural: "semanas", feminine: true },
  month: { singular: "mes", plural: "meses", feminine: false },
  year: { singular: "año", plural: "años", feminine: false },
};

const TEENS = [
  "diez",
  "once",
  "doce",
  "trece",
  "catorce",
  "quince",
  "dieciséis",
  "diecisiete",
  "dieciocho",
  "diecinueve",
];

const TENS = [
  "",
  "",
  "veinte",
  "treinta",
  "cuarenta",
  "cincuenta",
  "sesenta",
  "setenta",
  "ochenta",
  "noventa",
];

const ONES = ["", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve"];

const toSpanishNumber = (value: number, feminine = false): string => {
  if (value <= 0) return "";
  if (value === 1) return feminine ? "una" : "un";
  if (value < 10) {
    const word = ONES[value];
    if (value === 1) return word;
    return word;
  }
  if (value < 20) return TEENS[value - 10];
  if (value < 30) {
    if (value === 20) return "veinte";
    const unit = value - 20;
    return unit === 1 ? "veintiuno" : `veinti${ONES[unit]}`;
  }
  if (value < 100) {
    const ten = Math.floor(value / 10);
    const one = value % 10;
    if (one === 0) return TENS[ten];
    if (ten === 2) return one === 1 ? "veintiuno" : `veinti${ONES[one]}`;
    return `${TENS[ten]} y ${ONES[one]}`;
  }
  if (value === 100) return "cien";
  return String(value);
};

const formatUnitPhrase = (amount: number, unit: TimeUnit): string => {
  if (amount === 1) {
    const article = unit.feminine ? "una" : "un";
    return `${article} ${unit.singular}`;
  }
  return `${toSpanishNumber(amount)} ${unit.plural}`;
};

const parseDepositDate = (date: string | number | Date | null | undefined) => {
  if (date == null || date === "") return null;

  let parsed = moment.utc(date).local();
  if (!parsed.isValid() && typeof date === "string") {
    parsed = moment(date).local();
  }
  if (!parsed.isValid() && typeof date === "number") {
    parsed = moment(date).local();
  }

  return parsed.isValid() ? parsed : null;
};

/**
 * Devuelve texto relativo en español: "hace tres horas", "hace una semana", etc.
 */
export const formatTimeAgoSpanish = (
  date: string | number | Date | null | undefined
): string | null => {
  const then = parseDepositDate(date);
  if (!then) return null;

  const now = moment();
  if (then.isAfter(now)) return null;

  const minutes = now.diff(then, "minutes");
  if (minutes < 1) return "hace un momento";

  const hours = now.diff(then, "hours");
  const days = now.diff(then, "days");
  const weeks = now.diff(then, "weeks");
  const months = now.diff(then, "months");
  const years = now.diff(then, "years");

  if (years >= 1) {
    return `hace ${formatUnitPhrase(years, TIME_UNITS.year)}`;
  }
  if (months >= 1) {
    return `hace ${formatUnitPhrase(months, TIME_UNITS.month)}`;
  }
  if (weeks >= 1) {
    return `hace ${formatUnitPhrase(weeks, TIME_UNITS.week)}`;
  }
  if (days >= 1) {
    return `hace ${formatUnitPhrase(days, TIME_UNITS.day)}`;
  }
  if (hours >= 1) {
    return `hace ${formatUnitPhrase(hours, TIME_UNITS.hour)}`;
  }
  return `hace ${formatUnitPhrase(minutes, TIME_UNITS.minute)}`;
};

export default formatTimeAgoSpanish;
